import { useMemo, useState } from 'react';
import { TableColumnsType, Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import { useNavigate } from 'react-router-dom';
import { Filter, Menu as MenuIcon } from 'iconsax-react';
import { useGetProductsQuery } from '../../store/services/products';
import {
  setModalId,
  setPage,
  usePublicState,
} from '../../store/slices/publicSlice';
import Button from '../atoms/Button';
import Search from '../categories/Search';
import { STATUS_COLOR } from '../../constants/system';
import CustomTable from '../atoms/CustomTable';
import { useDispatch } from 'react-redux';
import FiltersModal from './FiltersModal';
import { useProductsState } from '../../store/slices/ProductsSlice';
import TableWrapper from '../atoms/TableWrapper';
import ActivityLogsPopup, {
  ActivityLogsPopupData,
} from '../AdminLogs/ActivityLogsPopup';

type StatusColorKey = keyof typeof STATUS_COLOR;

export interface ProductStatus {
  id: string;
  code: string;
  name: string;
}

export interface ProductTag {
  id: string;
  name: string;
}

export interface ProductActivityItem {
  id: string;
  productId: string;
  productName: string;
  productSerial: string;
  imageUrl: string;
  quantity: number;
  createdBy: string;
  dateCreated: string;
  status: ProductStatus;
  tag: ProductTag;
}

const ProductsTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search, modalId, page } = usePublicState();
  const { filters } = useProductsState();
  const [logsModalData, setLogsModalData] =
    useState<ActivityLogsPopupData | null>(null);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const {
    data: rawData,
    isLoading,
    isFetching,
    isError,
  } = useGetProductsQuery(
    {
      page,
      search: search?.product,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate,
      tag: filters?.tags?.toString(),
    },
    { refetchOnMountOrArgChange: true }
  );

  dayjs.locale(language);

  const columns: TableColumnsType<any> = useMemo(
    () => [
      {
        title: (
          <span className="!font-semibold text-[16px] leading-none text-[#090909] dark:text-white">
            {t('product.image', 'Image')}
          </span>
        ),
        dataIndex: 'image',
        key: 'image',
        width: 100,
        render: (value: string) => (
          <img src={value} className="w-10 h-10 object-cover rounded" alt="" />
        ),
      },
      {
        title: (
          <span className="!font-semibold text-[16px] leading-none text-[#090909] dark:text-white">
            {t('product.name', 'Product name')}
          </span>
        ),
        dataIndex: 'productName',
        key: 'productName',
        render: (text: string) => (
          <span className="font-normal text-sm leading-none text-[#090909]">
            {text}
          </span>
        ),
      },
      {
        title: (
          <span className="!font-semibold text-[16px] leading-none text-[#090909] dark:text-white">
            {t('product.createdBy', 'Created by')}
          </span>
        ),
        dataIndex: 'mail',
        key: 'mail',
        width: 160,
      },
      {
        title: (
          <span className="!font-semibold text-[16px] leading-none text-[#090909] dark:text-white">
            {t('product.avQuantity', 'AV. quantity')}
          </span>
        ),
        key: 'avQan',
        dataIndex: 'avQan',
        render: (value: string | number) =>
          value !== '-' ? `${value} item` : value,
      },
      {
        title: (
          <span className="!font-semibold text-[16px] leading-none text-[#090909] dark:text-white">
            {t('product.dateCreated', 'Date created')}
          </span>
        ),
        key: 'dateCreated',
        dataIndex: 'dateCreated',
        width: 160,
      },
      {
        title: (
          <span className="!font-semibold text-[16px] leading-none text-[#090909] dark:text-white">
            {t('home.status', 'Status')}
          </span>
        ),
        key: 'status',
        dataIndex: 'status',
        render: (value: ProductStatus) => {
          const statusStyle =
            value?.code && value.code in STATUS_COLOR
              ? STATUS_COLOR[value.code as StatusColorKey]
              : { text: '#4DA167' };
          return (
            <span
              style={{ color: statusStyle?.text }}
              className="text-sm font-normal whitespace-nowrap"
            >
              {value?.name}
            </span>
          );
        },
      },
      {
        title: (
          <span className="!font-semibold text-[16px] leading-none text-[#090909] dark:text-white">
            {t('common.actions', 'Actions')}
          </span>
        ),
        key: 'actions',
        width: 100,
        render: () => (
          <Dropdown
            menu={{ items: [{ key: 'view', label: 'View' }] }}
            trigger={['click']}
            placement="bottomRight"
          >
            <MenuIcon
              size="24"
              className="text-gray-500 cursor-pointer mx-auto"
            />
          </Dropdown>
        ),
      },
    ],
    [t]
  );

  const data = useMemo(() => {
    return (
      rawData?.payload?.content?.map((item: ProductActivityItem) => ({
        id: item.id,
        productId: item.productId,
        image: item.imageUrl,
        mail: item.createdBy,
        productName: item.productName || '-',
        productTag: item.tag,
        status: item.status,
        avQan: item.quantity ?? '-',
        dateCreated: item.dateCreated
          ? dayjs(item.dateCreated).format('DD MMM YYYY')
          : '-',
      })) ?? []
    );
  }, [rawData?.payload?.content, language]);

  return (
    <TableWrapper
      filters={
        <div
          className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto"
          data-testid="products-table-actions"
        >
          {!isError && (
            <Search searchName="product" inputWidth="w-full sm:w-64 lg:w-80" />
          )}
          <Button
            size="md"
            className="!rounded-xl !border-light-input-border !px-4 !py-3 !gap-2.5 !bg-white !text-light-text-primary whitespace-nowrap w-full sm:w-auto justify-center"
            variant="outline"
            icon={
              <Filter
                size={18}
                variant="Outline"
                color="#090909"
                className="dark:text-white"
              />
            }
            onClick={() => dispatch(setModalId({ id: 'PRODUCTS_FILTERS' }))}
            data-testid="products-table-filter-btn"
          >
            {t('product.filterBtn')}
          </Button>
        </div>
      }
      title={t('product.productsRequests')}
      subTitle={t('product.productsRequestsTableDesc')}
    >
      <>
        <CustomTable
          onRowClick={(_, record: ProductActivityItem) => {
            if (record?.status?.code?.toUpperCase() === 'DELETED') {
              setLogsModalData({
                id: record?.productId || record?.id,
                type: 'PRODUCT',
              });
              return;
            }
            const query = record?.productId
              ? `?productId=${record.productId}`
              : '';
            navigate(`/products/request-details/${record?.id}${query}`);
          }}
          columns={columns}
          loading={isLoading || isFetching}
          dataSource={data}
          rowClassName={(record, index) =>
            index % 2 !== 0
              ? 'cursor-pointer [&>td]:!bg-[#F9F9F9] dark:[&>td]:!bg-gray-800/50 [&>td:first-child]:rounded-l-[12px] [&>td:last-child]:rounded-r-[12px] [&>td]:transition-colors'
              : 'cursor-pointer [&>td]:!bg-white dark:[&>td]:!bg-gray-900 [&>td]:transition-colors'
          }
          currentPage={page}
          onPageChange={(page) => {
            dispatch(setPage(page - 1));
          }}
          totalElements={rawData?.payload?.totalElements || 0}
          scroll={{ x: 'max-content' }}
        />

        {modalId === 'PRODUCTS_FILTERS' && <FiltersModal />}

        <ActivityLogsPopup
          isOpen={Boolean(logsModalData)}
          onClose={() => setLogsModalData(null)}
          data={logsModalData}
        />
      </>
    </TableWrapper>
  );
};

export default ProductsTable;
